import React, { useState, useCallback } from 'react';
import { CustomInputProps } from '../../../types/summarizer';
import { useSummarizer, getSourceTypeFromFile } from '../../../core/store/useSummarizer';
import { useDropzone } from 'react-dropzone';
import { VscLoading } from 'react-icons/vsc';
import { FaFilePdf } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import { ChevronDown, Link, FileUp } from 'lucide-react';
import { Button, ErrorAlert, Input, parseErrorMessage, SummaryOptionsBar, ProcessingButton } from '@components/ui';
import { ProgressBar } from '@components/ui';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SOURCE_TYPES } from '../../../constants/summarizer';
import { SafeImage } from '@components/ui/safe-image';
import { pluginUrl } from '../../../lib/utils';

const PDFInputSection: React.FC<CustomInputProps> = ({ config, className = '' }) => {
  const {
    selectedFile,
    fileError,
    uploadProgress,
    uploadedUrl,
    isUploading,
    summaryType,
    tone,
    setSelectedFile,
    validateFile,
    clearError,
    setSummaryType,
    setTone,
    uploadFile,
    summarize,
    isLoading: storeLoading,
    setFileError,
  } = useSummarizer();

  // Local state for upload metadata
  const [title, setTitle] = useState('');

  // New state for the Paste Link dialog
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // State to track if dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const { isValid, error } = validateFile(file, {
        acceptedTypes: config.inputs.file?.acceptedTypes,
        maxSize: config.inputs.file?.maxSize ? config.inputs.file.maxSize : undefined,
      });

      if (!isValid) {
        setFileError(error || 'File validation failed');
        return;
      }

      setSelectedFile(file);
      setTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for title

      // Upload file automatically
      uploadFile(file).catch((error: Error) => {
        console.error('Upload failed:', error);
      });
    },
    [validateFile, config.inputs.file, setSelectedFile, uploadFile]
  );

  const handleLinkSubmit = async () => {
    if (!linkUrl.trim()) return;

    try {
      await summarize({
        link_or_id: linkUrl.trim(),
        source: SOURCE_TYPES.URL,
        summaryType,
        tone,
      });

      setIsLinkDialogOpen(false);
    } catch (error) {
      // Error handled by store
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled: storeLoading || isUploading,
    noClick: isDropdownOpen, // Disable click when dropdown is open
  });

  const handleSubmit = async () => {
    if (!uploadedUrl || !selectedFile) return;

    try {
      const sourceType = getSourceTypeFromFile(selectedFile.name);

      await summarize({
        link_or_id: uploadedUrl,
        source: sourceType,
        title,
        extension: selectedFile.name?.split('.').pop(),
        summaryType,
        tone,
      });
    } catch (error) {
      // Error handled by store
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setTitle('');
    clearError();
  };

  const canSubmit = uploadedUrl && title.trim() && !storeLoading && !isUploading;
  const hasFile = selectedFile && uploadProgress === 100;
  const loading = storeLoading;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
      <div className={`pdf-input-section space-y-8 ${className}`}>
        {/* Input Section */}
        <div className="max-w-4xl relative mx-auto">
          {/* Error Display */}
          {fileError && (
            <ErrorAlert
              {...parseErrorMessage(fileError)}
              onDismiss={() => {
                clearError();
                if (fileError) setFileError('');
              }}
              className="mb-4 animate-in fade-in slide-in-from-top-2"
            />
          )}

          {/* File Upload Area */}
          {!hasFile && (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl min-h-[400px] flex flex-col items-center justify-center p-8 cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              } ${isUploading || isDropdownOpen ? 'pointer-events-none opacity-50' : ''}`}
            >
              <div className="hidden lg:!block absolute min-w-[240px] -top-[100px] -right-28">
                <div className="relative w-full h-full">
                  <SafeImage
                    src={`${pluginUrl}assets/images/custom-bottom-arrow.svg`}
                    alt=""
                    fallback={<div></div>}
                    className="w-28 absolute left-0"
                  />
                  <SafeImage
                    src={`${pluginUrl}assets/images/drag-drop-pdf-txt.svg`}
                    alt=""
                    fallback={<div></div>}
                    className="w-48 absolute right-0"
                  />
                </div>
              </div>

              <input {...getInputProps()} />

              {isUploading ? (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 flex items-center justify-center mb-4">
                    <VscLoading className="animate-spin text-4xl text-blue-600" />
                  </div>
                  <p className="text-lg font-medium text-gray-700 mb-2">Uploading...</p>
                  <div className="w-64 mb-4">
                    <ProgressBar value={uploadProgress} />
                  </div>
                  <p className="text-sm text-gray-500">{uploadProgress}% complete</p>
                </div>
              ) : (
                <>
                  <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-2xl mb-6">
                    <FaFilePdf className="text-4xl text-red-600" />
                  </div>

                  <p className="text-xl font-semibold text-gray-700 mb-2">
                    {isDragActive ? 'Drop your PDF here' : 'Click to upload or Drag PDF here'}
                  </p>

                  <div
                    className="flex items-center gap-0 mt-6 shadow-sm rounded-lg"
                    style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10 }}
                  >
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        open();
                      }}
                      className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-l-lg !rounded-r-none flex items-center gap-2"
                    >
                      <FileUp className="w-5 h-5" />
                      <span>Select Files</span>
                    </Button>
                    <DropdownMenu onOpenChange={setIsDropdownOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          onClick={(e) => e.stopPropagation()}
                          className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-3 rounded-r-lg !rounded-l-none border-l border-sky-400"
                        >
                          <ChevronDown className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56 !bg-sky-500 text-white">
                        <DialogTrigger asChild>
                          <DropdownMenuItem
                            className="!border-none !outline-none focus:outline-none hover:!bg-sky-600 !bg-sky-500 text-white hover:!text-white"
                            onSelect={() => {
                              setIsLinkDialogOpen(true);
                              setIsDropdownOpen(false);
                            }}
                          >
                            <Link className="mr-2 h-4 w-4" />
                            <span>Past Link</span>
                          </DropdownMenuItem>
                        </DialogTrigger>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {!!config.inputs?.file?.maxSize && (
                    <p className="text-sm text-gray-500 mt-4">
                      Maximum file size: {formatFileSize(config.inputs.file.maxSize)}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* File Preview and Controls */}
          {hasFile && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg">
                  <FaFilePdf className="text-2xl text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedFile?.name}</p>
                  <p className="text-sm text-gray-500">
                    PDF • {selectedFile ? formatFileSize(selectedFile.size) : ''}
                  </p>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-100 rounded-full border border-gray-300 transition-colors"
                >
                  <IoMdClose className="text-gray-500" />
                </button>
              </div>

              {/* Summary Options and Submit */}
              <div className="flex flex-col sm:!flex-row items-stretch sm:!items-center mt-4 gap-3">
                <SummaryOptionsBar
                  summaryType={summaryType}
                  tone={tone}
                  onSummaryTypeChange={setSummaryType}
                  onToneChange={setTone}
                  disabled={loading}
                  className="flex-1"
                  gap="md"
                />
                <ProcessingButton
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  isLoading={loading}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                  submitButtonText="Generate Summary"
                  className="sm:!ml-auto min-w-[180px]"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Paste Link Dialog */}
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Past Link</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com/document.pdf"
              className="w-full text-center"
            />
            <Button
              onClick={handleLinkSubmit}
              disabled={loading || !linkUrl.trim()}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? 'Generating...' : 'Generate Summary'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PDFInputSection;
